/**
 * /collections — the "Подборки" landing page.
 *
 * Lists the user's collections as cards with summary metrics (members,
 * platforms, total reach, base price). Each card supports the four common
 * actions: open the detail page, launch a new campaign, push members into
 * an existing campaign, delete.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, FolderPlus, Layers, Plus, Rocket, Trash2, Users } from "lucide-react";
import {
  useCampaigns,
  useCollections,
  useCreateCollection,
  useDeleteCollection,
  useExtendCampaignFromCollection,
  useLaunchCollection,
  usePatchCollection,
  type CampaignSummary,
  type CollectionSummary,
} from "@/shared/api/wb";
import { Button, Card, ConfirmModal, PlatformIcon, type Platform } from "@/shared/ui";
import { fmtNum, fmtRubFull } from "@/shared/lib/format";
import { cn } from "@/shared/lib/cn";

export function CollectionsPage() {
  const { data: collections } = useCollections();
  const create = useCreateCollection();

  return (
    <div className="p-7 pb-16">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-[11px] font-display font-semibold uppercase tracking-[0.16em] text-ink-40 mb-1">Сохранённые списки блогеров</div>
          <h1 className="font-display font-extrabold tracking-tight text-[28px] leading-tight">Подборки</h1>
        </div>
        <Button onClick={() => create.mutate({})}>
          <FolderPlus size={16} /> Новая подборка
        </Button>
      </div>

      {!collections && <div className="text-ink-40 py-10 text-center">Загрузка…</div>}
      {collections && collections.length === 0 && <EmptyState />}
      {collections && collections.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {collections.map((c) => <CollectionCard key={c.id} collection={c} />)}
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  const create = useCreateCollection();
  return (
    <div className="grid place-items-center py-20 px-4 text-center">
      <div className="w-14 h-14 rounded-md bg-ink-6 grid place-items-center text-ink-40 mb-4">
        <Layers size={26} />
      </div>
      <div className="font-display font-bold text-[18px] mb-1.5">Пока ни одной подборки</div>
      <div className="text-[13px] text-ink-60 leading-relaxed max-w-[420px] mb-4">
        В каталоге блогеров жмите «+» на карточке — каналы будут складываться в активную подборку.
        Подборки удобно использовать как кандидатов под новую РК или как долгосрочный пул.
      </div>
      <Button onClick={() => create.mutate({})}>
        <FolderPlus size={16} /> Создать подборку
      </Button>
    </div>
  );
}

function CollectionCard({ collection }: { collection: CollectionSummary }) {
  const navigate = useNavigate();
  const patch = usePatchCollection();
  const launch = useLaunchCollection();
  const del = useDeleteCollection();
  const [extOpen, setExtOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const setActive = () => patch.mutate({ id: collection.id, patch: { is_active: true } });

  async function onLaunch() {
    const res = await launch.mutateAsync(collection.id);
    navigate(res.wizard_step || "/campaign/new/product");
  }

  return (
    <Card className={cn("!p-5 flex flex-col gap-4 cursor-pointer hover:border-ink/30",
      collection.is_active && "border-lime ring-1 ring-lime")}
      onClick={() => navigate(`/collections/${collection.id}`)}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <div className="font-display font-bold text-[17px] tracking-[-0.01em] truncate">{collection.name}</div>
            {collection.is_active && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-pill bg-lime text-[var(--fg-on-accent)] text-[10px] font-mono font-bold whitespace-nowrap">
                <CheckCircle2 size={10} strokeWidth={3} /> Активная
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-[12px] text-ink-40">
            <Users size={11} /> {collection.members_count} {pluralBloggers(collection.members_count)}
            {collection.platforms.length > 0 && (
              <span className="flex items-center gap-1 ml-2">
                {collection.platforms.slice(0, 4).map((p) => (
                  <PlatformIcon key={p} platform={p as Platform} size={11} mono />
                ))}
              </span>
            )}
          </div>
        </div>
        <button onClick={(e) => { e.stopPropagation(); setDeleteConfirm(true); }}
          className="w-7 h-7 grid place-items-center rounded-md text-ink-40 hover:text-danger hover:bg-danger/10">
          <Trash2 size={13} />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Stat label="Охват" value={collection.total_reach ? fmtNum(collection.total_reach) : "—"} />
        <Stat label="Базовый бюджет" value={fmtRubFull(collection.base_total_price)} />
      </div>

      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
        {!collection.is_active && (
          <button onClick={setActive}
            className="text-[12px] text-link hover:underline">
            Сделать активной
          </button>
        )}
        <div className="ml-auto flex gap-2">
          <button onClick={() => setExtOpen(true)}
            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-pill border border-ink-12 text-[12px] font-display font-semibold text-ink-60 hover:text-ink hover:border-ink/40">
            <Plus size={11} /> В РК
          </button>
          <Button size="sm" onClick={onLaunch} disabled={launch.isPending || collection.members_count === 0}>
            <Rocket size={12} /> Запустить РК
          </Button>
        </div>
      </div>

      {extOpen && <ExtendToCampaignModal collectionId={collection.id} onClose={() => setExtOpen(false)} />}
      {deleteConfirm && (
        <ConfirmModal
          title="Удалить подборку?"
          message={`«${collection.name}» будет удалена без возможности восстановления.`}
          confirmLabel="Удалить"
          danger
          onConfirm={() => { setDeleteConfirm(false); del.mutate(collection.id); }}
          onCancel={() => setDeleteConfirm(false)}
        />
      )}
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-wider text-ink-40">{label}</div>
      <div className="font-display font-bold text-[15px] mt-0.5 tabular-nums">{value}</div>
    </div>
  );
}

export function ExtendToCampaignModal({ collectionId, bloggerIds, onClose }: {
  collectionId: string;
  bloggerIds?: string[];
  onClose: () => void;
}) {
  const { data: campaigns } = useCampaigns();
  const extend = useExtendCampaignFromCollection();
  const eligible = (campaigns ?? []).filter((c: CampaignSummary) => c.status !== "draft");
  const [result, setResult] = useState<string | null>(null);

  const isSingle = bloggerIds && bloggerIds.length === 1;

  async function add(campaignId: string) {
    const res = await extend.mutateAsync({ collectionId, campaignId, bloggerIds });
    const parts: string[] = [];
    if (res.added > 0) parts.push(`Добавлено ${res.added} ${pluralBloggers(res.added)}`);
    if (res.skipped_already_in_campaign > 0) parts.push(`${res.skipped_already_in_campaign} уже в этой РК`);
    if (res.skipped_missing > 0) parts.push(`${res.skipped_missing} не найдено`);
    setResult(parts.length > 0 ? parts.join(" · ") : "Нет блогеров для добавления");
  }

  return (
    <div className="fixed inset-0 z-[110] grid place-items-center"
      style={{ background: "rgba(28,28,28,0.50)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-[var(--bg)] rounded-lg shadow-lg p-6 w-[min(560px,92vw)] fade-up">
        {result ? (
          <>
            <h3 className="font-display font-bold text-[18px] mb-3">Готово</h3>
            <p className="text-[14px] text-ink-60 mb-5">{result}</p>
            <div className="flex justify-end">
              <Button onClick={onClose}>Закрыть</Button>
            </div>
          </>
        ) : (
          <>
            <h3 className="font-display font-bold text-[18px] mb-4">
              {isSingle ? "Добавить канал в РК" : "Добавить подборку в РК"}
            </h3>
            <p className="text-[12.5px] text-ink-40 mb-3">
              {isSingle ? "Выберите кампанию для этого канала" : "Выберите кампанию — все каналы подборки будут добавлены"}
            </p>
            <div className="grid gap-1 max-h-[60vh] overflow-y-auto">
              {eligible.length === 0 && (
                <div className="text-center text-ink-40 py-6 text-[13px]">Нет запущенных РК — сначала запустите кампанию.</div>
              )}
              {eligible.map((c: CampaignSummary) => (
                <button key={c.id} onClick={() => add(c.id)} disabled={extend.isPending}
                  className="grid grid-cols-[1fr_auto] items-center gap-3 p-3 text-left rounded-md hover:bg-ink-6 disabled:opacity-60">
                  <div>
                    <div className="font-display font-bold text-[14px]">{c.product}</div>
                    <div className="text-[12px] text-ink-40">{c.type === "seeding" ? "Посев" : "Натив"} · {c.platforms.join(", ")} · {c.placements_count} каналов</div>
                  </div>
                  <Plus size={14} className="text-ink-40" />
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function pluralBloggers(n: number): string {
  if (n % 10 === 1 && n % 100 !== 11) return "блогер";
  if ([2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100)) return "блогера";
  return "блогеров";
}
